import React, {FC, useMemo, useState} from 'react'
import {Form, Pagination, Row, Col} from 'react-bootstrap'
import {useNavigate} from 'react-router-dom'
import {CountryOverviewRow, HsStatus} from '../core/_models'
import {getNameByISO3} from '../../../../libs/misc'

// ──────────────────────────────────────────────────────────────────────────────
// Status badge
// ──────────────────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  HsStatus,
  {color: string; bg: string; title: string; icon: string}
> = {
  green: {
    color: '#155724',
    bg: '#d4edda',
    title: 'Synced to TINA',
    icon: 'fa-solid fa-database',
  },
  grey: {
    color: '#495057',
    bg: '#e2e3e5',
    title: 'Available on Comtrade – not yet synced',
    icon: 'fa-solid fa-database',
  },
  yellow: {
    color: '#856404',
    bg: '#fff3cd',
    title: 'Update available on Comtrade',
    icon: 'fa-solid fa-database',
  },
  red: {
    color: '#721c24',
    bg: '#f8d7da',
    title: 'Not available on Comtrade',
    icon: 'fa-solid fa-database',
  },
}

type StatusBadgeProps = {hsLabel: string; status: HsStatus; countryCode: string; year: number}

const StatusBadge: FC<StatusBadgeProps> = ({hsLabel, status, countryCode, year}) => {
  const navigate = useNavigate()
  const cfg = STATUS_CONFIG[status]

  const handleClick = () => {
    navigate(`/trade-data/${countryCode}?year=${year}&hs_vr=${hsLabel}&source_type=comtrade`)
  }

  return (
    <span
      title={`${hsLabel} ${year}: ${cfg.title} — click for detail`}
      onClick={handleClick}
      role='button'
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleClick()
        }
      }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '2px 7px',
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: 600,
        color: cfg.color,
        backgroundColor: cfg.bg,
        border: `1px solid ${cfg.color}22`,
        cursor: 'pointer',
        userSelect: 'none',
        whiteSpace: 'nowrap',
      }}
    >
      <i className={cfg.icon} style={{fontSize: '10px'}} />
      {hsLabel}
    </span>
  )
}

// A single pulsing placeholder the same footprint as a real StatusBadge —
// shown for any (country, hs, year) cell that hasn't streamed in yet. Uses
// Bootstrap 5.3's built-in `.placeholder` / `.placeholder-glow` utilities
// (no extra CSS needed); `.placeholder-glow` must live on an ancestor for
// the pulse animation to apply, so callers wrap rows with it.
const SkeletonBadge: FC = () => (
  <span
    className='placeholder rounded'
    style={{width: '30px', height: '16px', display: 'inline-block'}}
  />
)

const SKELETON_ROW_COUNT = 8

// Freezes the "Reporter" column so it stays put when the table's scrolled
// horizontally to see later years — otherwise it's easy to lose track of
// which row you're looking at. `left`/`zIndex`/backgroundColor need to be
// set per-cell (not just on the column as a whole): position: sticky only
// pins whatever element it's applied to, and it needs an opaque background
// or the columns scrolling underneath show through.
const STICKY_COL_STYLE: React.CSSProperties = {
  position: 'sticky',
  left: 0,
  width: '200px',
  minWidth: '200px',
  boxShadow: '2px 0 4px -2px rgba(0, 0, 0, 0.25)',
}

// ──────────────────────────────────────────────────────────────────────────────
// Legend
// ──────────────────────────────────────────────────────────────────────────────

const Legend: FC = () => (
  <div className='d-flex align-items-center flex-wrap gap-3 mb-4' style={{fontSize: '12px'}}>
    <strong className='me-1'>Legend:</strong>
    {(Object.entries(STATUS_CONFIG) as [HsStatus, (typeof STATUS_CONFIG)[HsStatus]][]).map(
      ([key, cfg]) => (
        <span
          key={key}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            padding: '2px 8px',
            borderRadius: '4px',
            color: cfg.color,
            backgroundColor: cfg.bg,
            border: `1px solid ${cfg.color}22`,
            fontWeight: 500,
          }}
        >
          <i className={cfg.icon} style={{fontSize: '11px'}} />
          {cfg.title}
        </span>
      )
    )}
  </div>
)

// ──────────────────────────────────────────────────────────────────────────────
// Main table component
// ──────────────────────────────────────────────────────────────────────────────

const HS_VERSIONS: ('H4' | 'H5' | 'H6')[] = ['H4', 'H5', 'H6']
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

type Props = {
  rows: CountryOverviewRow[]
  years: number[]
  isLoading: boolean
}

const CountryOverviewTable: FC<Props> = ({rows, years, isLoading}) => {
  const [search, setSearch] = useState('')
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(20)

  const filtered = useMemo(() => {
    if (!rows) return []
    if (!search.trim()) return rows
    const q = search.toLowerCase()
    return rows.filter((r) => getNameByISO3(r.tina_rep).toLowerCase().includes(q))
  }, [rows, search])

  const totalPages = Math.ceil(filtered.length / pageSize)
  const paginated = filtered.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize)

  const handleSearch = (val: string) => {
    setSearch(val)
    setPageIndex(0)
  }

  const handlePageSize = (val: number) => {
    setPageSize(val)
    setPageIndex(0)
  }

  const paginationRange = () => {
    const current = pageIndex + 1
    const maxShow = 3
    const half = Math.floor(maxShow / 2)
    let start = Math.max(1, current - half)
    let end = Math.min(totalPages, current + half)
    if (end - start + 1 < maxShow) {
      if (current - start >= half) start = Math.max(1, end - maxShow + 1)
      else end = Math.min(totalPages, start + maxShow - 1)
    }
    const range: (number | '...')[] = []
    if (start > 1) {
      range.push(1)
      if (start > 2) range.push('...')
    }
    for (let i = start; i <= end; i++) range.push(i)
    if (end < totalPages) {
      if (end < totalPages - 1) range.push('...')
      range.push(totalPages)
    }
    return range
  }

  // Nothing to show and nothing coming — genuinely empty state.
  if ((!rows || rows.length === 0) && !isLoading) return null

  const firstIdx = pageIndex * pageSize + 1
  const lastIdx = pageIndex * pageSize + paginated.length

  return (
    <>
      <Legend />

      {/* Search + page-size controls */}
      <Row className='mb-3 align-items-center justify-content-between'>
        <Col lg={5} md={6} sm={12} className='mb-2 mb-md-0'>
          <div className='d-flex align-items-center gap-1 text-muted' style={{fontSize: '13px'}}>
            <i className='fa-solid fa-circle text-success fs-9 me-1' />
            {rows.length === 0 && isLoading
              ? 'Loading countries…'
              : `Showing ${firstIdx}–${lastIdx} of ${filtered.length} countries`}
          </div>
        </Col>
        <Col lg={7} md={6} sm={12}>
          <div className='d-flex gap-2 justify-content-md-end'>
            <div style={{position: 'relative', minWidth: '200px'}}>
              <Form.Control
                type='text'
                placeholder='Search country…'
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                size='sm'
                style={{paddingRight: '32px'}}
              />
              <i
                className='fa-solid fa-magnifying-glass text-muted'
                style={{position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px'}}
              />
            </div>
            <div className='d-flex align-items-center gap-1' style={{fontSize: '13px', whiteSpace: 'nowrap'}}>
              <span className='text-muted'>Show</span>
              <Form.Select
                size='sm'
                value={pageSize}
                onChange={(e) => handlePageSize(Number(e.target.value))}
                style={{width: '70px'}}
              >
                {PAGE_SIZE_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>
        </Col>
      </Row>

      {/* Scrollable table wrapper */}
      <div style={{overflowX: 'auto', borderRadius: '6px', border: '1px solid #dee2e6'}}>
        <table
          className='table table-bordered table-sm table-striped table-hover mb-0'
          style={{fontSize: '12px', minWidth: `${220 + years.length * HS_VERSIONS.length * 62}px`, tableLayout: 'fixed'}}
        >
          <thead>
            {/* Row 1: Reporter | Year groups */}
            <tr className='table-dark'>
              <th
                rowSpan={2}
                className='align-middle text-center'
                style={{
                  ...STICKY_COL_STYLE,
                  verticalAlign: 'middle',
                  zIndex: 3,
                  backgroundColor: '#1e1e2d',
                  color: '#fff',
                }}
              >
                Reporter
              </th>
              {years.map((year) => (
                <th
                  key={year}
                  colSpan={HS_VERSIONS.length}
                  className='text-center'
                  style={{padding: '8px 4px'}}
                >
                  {year}
                </th>
              ))}
            </tr>
            {/* Row 2: H4 H5 H6 per year */}
            <tr className='table-dark'>
              {years.map((year) =>
                HS_VERSIONS.map((hs) => (
                  <th
                    key={`${year}-${hs}`}
                    className='text-center'
                    style={{width: '62px', minWidth: '62px', fontWeight: 500, fontSize: '11px', padding: '4px 2px'}}
                  >
                    {hs}
                  </th>
                ))
              )}
            </tr>
          </thead>
          <tbody>
            {isLoading && paginated.length === 0
              ? // No country rows have streamed in yet — draw a fixed number
                // of fully-skeleton rows so the grid appears immediately
                // instead of a blank table while the job spins up.
                Array.from({length: SKELETON_ROW_COUNT}).map((_, i) => (
                  <tr key={`skeleton-row-${i}`} className='placeholder-glow'>
                    <td
                      style={{
                        ...STICKY_COL_STYLE,
                        zIndex: 1,
                        // Solid colors only — a translucent stripe (Bootstrap's
                        // default is rgba(0,0,0,.05)) lets whatever's scrolled
                        // underneath this sticky cell show through, which is
                        // what caused the badge/text "collision" look.
                        backgroundColor: i % 2 === 0 ? '#f2f2f2' : '#fff',
                      }}
                    >
                      <span className='placeholder col-8' />
                    </td>
                    {years.map((year) =>
                      HS_VERSIONS.map((hs) => (
                        <td
                          key={`skeleton-${year}-${hs}`}
                          className='text-center'
                          style={{padding: '5px 3px', width: '62px'}}
                        >
                          <SkeletonBadge />
                        </td>
                      ))
                    )}
                  </tr>
                ))
              : paginated.map((row, idx) => (
                  <tr key={row.tina_rep} className={isLoading ? 'placeholder-glow' : undefined}>
                    <td
                      className='fw-semibold'
                      style={{
                        ...STICKY_COL_STYLE,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        zIndex: 1,
                        backgroundColor: idx % 2 === 0 ? '#f2f2f2' : '#fff',
                      }}
                      title={getNameByISO3(row.tina_rep)}
                    >
                      {getNameByISO3(row.tina_rep)}
                      <span className='text-muted ms-1' style={{fontSize: '10px', fontWeight: 400}}>
                        ({row.tina_rep})
                      </span>
                    </td>
                    {years.map((year) =>
                      HS_VERSIONS.map((hs) => {
                        const status = row.data?.[year]?.[hs]
                        return (
                          <td key={`${year}-${hs}`} className='text-center' style={{padding: '5px 3px', width: '62px'}}>
                            {status ? (
                              <StatusBadge
                                hsLabel={hs}
                                status={status}
                                countryCode={row.tina_rep}
                                year={year}
                              />
                            ) : isLoading ? (
                              <SkeletonBadge />
                            ) : (
                              <span className='text-muted'>–</span>
                            )}
                          </td>
                        )
                      })
                    )}
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Row className='mt-3'>
          <Col className='d-flex justify-content-end'>
            <Pagination size='sm'>
              <Pagination.Prev
                onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                disabled={pageIndex === 0}
              />
              {paginationRange().map((page, i) => {
                if (page === '...') return <Pagination.Ellipsis key={i} />
                return (
                  <Pagination.Item
                    key={i}
                    active={page === pageIndex + 1}
                    onClick={() => setPageIndex((page as number) - 1)}
                  >
                    {page}
                  </Pagination.Item>
                )
              })}
              <Pagination.Next
                onClick={() => setPageIndex((p) => Math.min(totalPages - 1, p + 1))}
                disabled={pageIndex >= totalPages - 1}
              />
            </Pagination>
          </Col>
        </Row>
      )}
    </>
  )
}

export {CountryOverviewTable}
